/**
 * Shareable recap (see §9). Renders a clean, screenshot-friendly card to a
 * canvas — "Our room hit an N-streak!" with the colour sequence and players —
 * then offers native share or PNG download. No personal data beyond chosen
 * colours/names; child-appropriate by construction.
 */

import type { RoomView } from '../shared/protocol.js';

export async function makeRecapBlob(room: RoomView): Promise<Blob> {
  const W = 1080;
  const H = 1080;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  // Background
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, '#11151c');
  grad.addColorStop(1, '#0d1117');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#e9edf2';
  ctx.font = '700 56px system-ui, sans-serif';
  ctx.fillText('SPATIAL SIMON', W / 2, 150);

  // The big number
  ctx.fillStyle = '#1db954';
  ctx.font = '800 340px system-ui, sans-serif';
  ctx.fillText(String(room.bestStreak), W / 2, 560);

  ctx.fillStyle = '#e9edf2';
  ctx.font = '700 60px system-ui, sans-serif';
  ctx.fillText('streak!', W / 2, 660);

  // Player colour dots
  const players = room.players;
  const dotR = 46;
  const gap = 130;
  const totalW = (players.length - 1) * gap;
  let x = W / 2 - totalW / 2;
  const y = 820;
  for (const p of players) {
    ctx.beginPath();
    ctx.arc(x, y, dotR, 0, Math.PI * 2);
    ctx.fillStyle = p.colourHex;
    ctx.fill();
    ctx.font = '48px system-ui, sans-serif';
    ctx.fillText(p.icon, x, y + 18);
    x += gap;
  }

  ctx.fillStyle = '#9aa4b2';
  ctx.font = '500 40px system-ui, sans-serif';
  ctx.fillText(`${players.length} players · together`, W / 2, 960);
  ctx.fillText('news.motlogs.com', W / 2, 1015);

  return await new Promise<Blob>((resolve) =>
    canvas.toBlob((b) => resolve(b!), 'image/png'),
  );
}

/** Share natively if possible; otherwise download the PNG. */
export async function shareRecap(room: RoomView): Promise<void> {
  const blob = await makeRecapBlob(room);
  const file = new File([blob], `spatial-simon-${room.bestStreak}.png`, { type: 'image/png' });
  const nav = navigator as any;
  if (nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: 'Spatial Simon', text: `We hit a ${room.bestStreak}-streak!` });
      return;
    } catch {
      /* fall through to download */
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
